import * as z from 'zod';

// The merchant's own moderation queue, as the «Заявки» screen renders it.
//
// The wire shape lives in `@noor/eats-api` (`v1ChangeRequest`); this is the
// view model it maps into — see `lib/map-change-request.ts`.

/**
 * Where a request stands.
 *
 * Five states, and only two of them are verdicts:
 * - `pending` — waiting in the queue. The only state that can be withdrawn.
 * - `approved` — a moderator applied it; the entity is live.
 * - `rejected` — sent back for rework, usually with a `rejectionReason`.
 * - `withdrawn` — the merchant took it back (`POST .../cancel`).
 * - `stale` — the entity moved on under it (another request was approved
 *   first), so this one can no longer be applied. Not anyone's decision.
 *
 * `unknown` catches a state the panel does not model rather than failing the
 * row: a queue that renders nothing is worse than one row labelled vaguely.
 */
export const CHANGE_REQUEST_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'stale',
  'unknown',
] as const;
export const changeRequestStatusSchema = z.enum(CHANGE_REQUEST_STATUSES);
export type ChangeRequestStatus = z.infer<typeof changeRequestStatusSchema>;

/**
 * The statuses the screen offers as FILTERS, in the order the tabs show them.
 * `unknown` is absent — it is a fallback for unmodelled data, not something a
 * merchant would filter for.
 */
export const CHANGE_REQUEST_STATUS_FILTERS = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'stale',
] as const;
export type ChangeRequestStatusFilter = (typeof CHANGE_REQUEST_STATUS_FILTERS)[number];

/** What the request would do to the entity. `unknown` degrades like the status. */
export const CHANGE_REQUEST_OPERATIONS = [
  'create',
  'update',
  'delete',
  'transition',
  'unknown',
] as const;
export const changeRequestOperationSchema = z.enum(CHANGE_REQUEST_OPERATIONS);
export type ChangeRequestOperation = z.infer<typeof changeRequestOperationSchema>;

/**
 * The kinds of entity the gate covers. `unknown` again degrades rather than
 * failing — a new moderated entity type must not blank the screen.
 */
export const CHANGE_REQUEST_ENTITY_KINDS = [
  'restaurant',
  'product',
  'product_category',
  'promotion',
  'restaurant_banner',
  'product_banner',
  'modifier_group',
  'unknown',
] as const;
export const changeRequestEntityKindSchema = z.enum(CHANGE_REQUEST_ENTITY_KINDS);
export type ChangeRequestEntityKind = z.infer<typeof changeRequestEntityKindSchema>;

export const changeRequestSchema = z.object({
  id: z.string(),
  /** What kind of thing was submitted — drives the row's type label. */
  entityKind: changeRequestEntityKindSchema,
  /** Absent for a pending CREATE: there is no entity yet to point at. */
  entityId: z.string().optional(),
  /**
   * The entity's own name, resolved to a display string. The list read carries
   * it (`entity_name`, all locales); it is absent on a pending create, where
   * the screen falls back to the operation + type label.
   */
  entityName: z.string().optional(),
  operation: changeRequestOperationSchema,
  status: changeRequestStatusSchema,
  /** Moderation's reason for sending it back. Only ever set on `rejected`. */
  rejectionReason: z.string().optional(),
  /** `manual` (the panel) or `sync` (menu sync) — which version this is. */
  source: z.string().optional(),
  /** Raw backend timestamps; the screen formats them. */
  createdAt: z.string().optional(),
  reviewedAt: z.string().optional(),
});
export type ChangeRequest = z.infer<typeof changeRequestSchema>;

/** A page of the queue, plus the total the pager needs. */
export interface ChangeRequestPage {
  items: ChangeRequest[];
  total: number;
}
